import { useEffect, useCallback } from 'react';

/**
 * Warn before abandoning unsaved edits on a form.
 *
 * WHY IT IS SHAPED THIS WAY: the app mounts a plain <BrowserRouter> (App.js),
 * not a data router. React Router 6's `useBlocker` / `unstable_usePrompt` only
 * work inside a data router (createBrowserRouter + RouterProvider) and throw
 * otherwise, so they are not available to us without migrating every route.
 * This hook therefore guards the exits we CAN reach:
 *
 *   1. `guard(leaveFn)` — wrap any in-app "leave" control: the Back arrow, a
 *      Cancel button, a dialog's onClose. Shows the shared confirm and only
 *      runs `leaveFn` when the operator agrees to discard.
 *   2. `beforeunload` — closing the tab, reloading, or navigating away to an
 *      external URL while dirty gets the browser's native "Leave site?" prompt.
 *      (The browser controls that text; it cannot be customised.)
 *
 * NOT COVERED: the browser's own Back/Forward buttons. Intercepting those
 * needs the data-router migration above. This is a known gap, not an oversight
 * — the in-app Back arrow IS guarded, which is the control operators use.
 *
 * Usage:
 *   const { confirmDialogProps, confirm } = useConfirmDialog();
 *   const isDirty = JSON.stringify(form) !== JSON.stringify(loadedSnapshot);
 *   const guard = useUnsavedChangesGuard(isDirty, confirm);
 *   ...
 *   <IconButton onClick={() => guard(() => navigate(listBackTo))}>
 *   <ConfirmDialog {...confirmDialogProps} />
 *
 * @param {boolean}  isDirty  true when the form differs from what was loaded/saved
 * @param {Function} confirm  the `confirm` fn from useConfirmDialog()
 * @returns {Function} guard(leaveFn) — async; runs leaveFn if it is safe to leave
 */
export default function useUnsavedChangesGuard(isDirty, confirm) {
  // Native prompt for tab close / reload / external navigation.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to be set; the string itself is ignored.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const guard = useCallback(async (leaveFn) => {
    if (!isDirty) {
      // Clean form — leave immediately, no dialog. Keeps the common path
      // (open a record, look, go back) at one click.
      await leaveFn?.();
      return true;
    }
    const ok = await confirm({
      title: 'Discard your changes?',
      message: 'You have edits on this page that have not been saved. If you leave now they are lost.',
      confirmText: 'Discard changes',
      cancelText: 'Keep editing',
      severity: 'warning',
    });
    if (!ok) return false;
    await leaveFn?.();
    return true;
  }, [isDirty, confirm]);

  return guard;
}
